import React, { useState, useEffect } from 'react';
import './AddImage.css';
import { addImage, getAllImages, getCategories } from '../../firebase/dataService';

function AddImage({ onImageAdded }) {
  const [formData, setFormData] = useState({
    caption: '',
    category: '',
    extractedText: '',
    visible: true
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [existingCategories, setExistingCategories] = useState([]);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [nextNumber, setNextNumber] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('الرجاء اختيار ملف صورة');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
        return;
      }

      setImageFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    loadCategoriesAndNextNumber();
  }, []);

  const loadCategoriesAndNextNumber = async () => {
    try {
      const images = await getAllImages();
      
      // تحميل التصنيفات من Firebase
      const categories = await getCategories();
      setExistingCategories(categories);
      
      // حساب الرقم التالي
      const numbers = images
        .map(img => {
          // البحث عن أي رقم بعد كلمة "فائدة" (مع أو بدون كلمة "رقم")
          const match = img.caption?.match(/فائدة\s*(?:رقم\s*)?(\d+)/);
          return match ? parseInt(match[1]) : 0;
        })
        .filter(num => num > 0);
      
      const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
      const next = maxNumber + 1;
      setNextNumber(next);
      
      // تعيين العنوان الافتراضي بنفس صيغة البيانات الموجودة
      setFormData(prev => ({
        ...prev,
        caption: `فائدة رقم ${next}`
      }));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setShowCustomCategory(true);
      setFormData(prev => ({ ...prev, category: '' }));
    } else {
      setShowCustomCategory(false);
      setFormData(prev => ({ ...prev, category: value }));
    }
  };

  const handleCustomCategoryChange = (e) => {
    const value = e.target.value;
    setCustomCategory(value);
    setFormData(prev => ({ ...prev, category: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!imageFile) {
      alert('الرجاء اختيار صورة');
      return;
    }

    if (!formData.caption.trim()) {
      alert('الرجاء إدخال عنوان للتصميم');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(5);

      const timestamp = Date.now();
      const filename = `${timestamp}_${imageFile.name}`;

      setUploadProgress(10);

      // رفع الصورة إلى Cloudinary
      const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
      const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

      const formDataUpload = new FormData();
      formDataUpload.append('file', imageFile);
      formDataUpload.append('upload_preset', uploadPreset);
      formDataUpload.append('folder', 'salman-images');
      formDataUpload.append('public_id', filename.split('.')[0]);

      setUploadProgress(20);

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        {
          method: 'POST',
          body: formDataUpload
        }
      );

      setUploadProgress(60);

      if (!response.ok) {
        throw new Error('فشل رفع الصورة إلى Cloudinary');
      }

      const data = await response.json();
      const downloadURL = data.secure_url;

      setUploadProgress(80);

      const imageData = {
        caption: formData.caption.trim(),
        category: formData.category.trim() || 'غير مصنف',
        extractedText: formData.extractedText.trim(),
        filename: filename,
        url: downloadURL,
        visible: formData.visible,
        createdAt: new Date().toISOString()
      };

      await addImage(imageData);

      setUploadProgress(100);

      alert('تم إضافة التصميم بنجاح!');
      
      setFormData({
        caption: '',
        category: '',
        extractedText: '',
        visible: true
      });
      setImageFile(null);
      setImagePreview(null);
      setUploadProgress(0);
      setShowCustomCategory(false);
      setCustomCategory('');
      
      loadCategoriesAndNextNumber();

      if (onImageAdded) {
        onImageAdded();
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('حدث خطأ في رفع الصورة: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      caption: '',
      category: '',
      extractedText: '',
      visible: true
    });
    setImageFile(null);
    setImagePreview(null);
    setUploadProgress(0);
    setShowCustomCategory(false);
    setCustomCategory('');
  };

  return (
    <div className="add-image">
      <div className="add-image-container">
        <form onSubmit={handleSubmit} className="add-image-form">
          <div className="form-section">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              الصورة
            </h3>
            
            <div className="file-upload-area">
              {imagePreview ? (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="معاينة" />
                  <button 
                    type="button" 
                    className="remove-image-btn"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                    disabled={uploading}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '4px' }}>
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    إزالة
                  </button>
                </div>
              ) : (
                <label className="file-upload-label">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                  <div className="upload-placeholder">
                    <span className="upload-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                        <polyline points="13 2 13 9 20 9"></polyline>
                      </svg>
                    </span>
                    <p>اضغط لاختيار صورة</p>
                    <span className="upload-hint">أو اسحب الصورة هنا</span>
                  </div>
                </label>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              معلومات التصميم
            </h3>
            
            <div className="form-group">
              <label htmlFor="caption">العنوان *</label>
              <input
                type="text"
                id="caption"
                name="caption"
                value={formData.caption}
                onChange={handleInputChange}
                placeholder="مثال: فائدة رقم 1"
                required
                disabled={uploading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">التصنيف</label>
              <select
                id="category"
                name="category"
                value={showCustomCategory ? 'custom' : formData.category}
                onChange={handleCategoryChange}
                disabled={uploading}
                className="category-select"
              >
                <option value="">اختر تصنيف...</option>
                {existingCategories.map((cat, index) => (
                  <option key={index} value={cat}>{cat}</option>
                ))}
                <option value="custom">➕ إضافة تصنيف جديد</option>
              </select>
              
              {showCustomCategory && (
                <input
                  type="text"
                  value={customCategory}
                  onChange={handleCustomCategoryChange}
                  placeholder="أدخل التصنيف الجديد..."
                  disabled={uploading}
                  className="custom-category-input"
                  autoFocus
                />
              )}
            </div>

            <div className="form-group">
              <label htmlFor="extractedText">النص المستخرج</label>
              <textarea
                id="extractedText"
                name="extractedText"
                value={formData.extractedText}
                onChange={handleInputChange}
                placeholder="النص الموجود في الصورة (اختياري)"
                rows="6"
                disabled={uploading}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="visible"
                  checked={formData.visible}
                  onChange={handleInputChange}
                  disabled={uploading}
                />
                <span>إظهار التصميم للمستخدمين</span>
              </label>
            </div>
          </div>

          {uploading && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p>جاري الرفع... {uploadProgress}%</p>
            </div>
          )}

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={uploading || !imageFile}
            >
              {uploading ? (
                <>
                  <span className="spinner-small"></span>
                  جاري الرفع...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '6px' }}>
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  إضافة التصميم
                </>
              )}
            </button>
            
            <button 
              type="button" 
              className="clear-btn"
              onClick={handleClear}
              disabled={uploading}
            >
              🔄 مسح الحقول
            </button>
          </div>
        </form>

        <div className="add-image-tips">
          <h3>💡 نصائح</h3>
          <ul>
            <li>استخدم صور بجودة عالية للحصول على أفضل نتيجة</li>
            <li>حجم الصورة يجب أن يكون أقل من 5 ميجابايت</li>
            <li>يمكنك إخفاء التصميم مؤقتاً وإظهاره لاحقاً</li>
            <li>التصنيف يساعد المستخدمين في البحث والتصفية</li>
            <li>النص المستخرج يحسن من نتائج البحث</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default AddImage;
